import React, { useEffect, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { Grid, Text } from 'elements';
import { RootState } from 'modules';
import { showAlert, showConfirm } from 'components/modal/showConfirm';
import { completePayment, createPaymentLog, getCasePrice, getMentorAnswerPrice } from 'libs/api/case';
import {
  drillVerifyMobilePayment,
  drillVerifyPayment,
  getDrillPaymentInfo,
  getMentoringTicketData,
} from 'libs/api/learning';
import { verifyMobilePayment, verifyPayment } from 'libs/api/mentoring';
import { getUrlQuery, makeComma } from 'libs/utils';
import { CMentoringTicketName, TMentoringTicketInfo } from 'modules/mentoring/types';
import { TUser, setUserInfo } from 'modules/user';
import { CWindowWidth } from 'constants/base.const';
import { payMethods } from 'enums/base.enum';
import { ERoute } from 'enums/route.enum';
import { Button, Checkbox, Radio, Space, Spin } from 'antd';
import styled from 'styled-components';
import { Colors, SecondaryColor, device } from 'GlobalStyles';
import KakaoIcon from 'assets/icons/kakaopay.svg';
import NaverIcon from 'assets/icons/naverpay.svg';

const Purchase = () => {
  const payIcons: { [key: string]: any } = {
    kakao: KakaoIcon,
    naver: NaverIcon,
  };
  const currentUser = useSelector((state: RootState) => state.user.current_user, shallowEqual);
  const history = useHistory();
  const location = useLocation();
  const params: { type: string; sub_type: string } = useParams(); //path params 값을 가져옴 (purchase/:type 으로 path를 설정해두었기 때문에 type 값을 가져올 수 있다)
  const {
    season,
    id,
    p_type,
    complete,
    imp_uid,
    imp_success,
    merchant_uid,

    mentoring_type,
    mock_type,
  } = getUrlQuery(location.search); //현재 url 의 쿼리 스트링으로 상태 뽑아내기
  const [completeInfo, setCompleteInfo] = useState<{
    name: string;
    price: number;
    credit?: number;
    method: string;
  }>();
  const [method, setMethod] = useState<string>('');
  // const [useAll, setUseAll] = useState(false);
  const [isAgree, setIsAgree] = useState(false);
  const [price, setPrice] = useState<{
    name: string;
    price: number;
    items?: Array<any>;
  }>();
  const [credit, setCredit] = useState(0);
  const [mentoringTicketsData, setMentoringTicketsData] = useState<Array<TMentoringTicketInfo>>();
  const EXCEL_VIDEO_PRICE = 300000;
  const INTERVIEW_MASTER_CLASS_BASIC_PRICE = 110000;
  const FINANCE_MENTORING_PRICE_60MINUTES = 300000;
  const FINANCE_FRAMEWORKS_PRICE = 30000;
  const dispatch = useDispatch();

  //처음 랜더 됐을 때 불러오는 상품 정보로 setPrice, setCredit
  const getProduct = async () => {
    if (params.type === 'finance_mentoring_ticket') {
      const { data } = await getMentoringTicketData({ field: { mentoring_field: 'finance' } });
      setMentoringTicketsData(data);
      setPrice({
        name: CMentoringTicketName[mentoring_type],
        price: Number(
          data.find(
            (el: TMentoringTicketInfo) => el.mentoring_ticket_name === CMentoringTicketName[mentoring_type],
          ).mentoring_ticket_price,
        ),
      });
    }
    if (params.type === 'finance_frameworks') {
      setPrice({
        name: `Finance Frameworks 무제한 이용권`,
        price: FINANCE_FRAMEWORKS_PRICE,
      });
    }

    if (params.type === 'mentoring_ticket') {
      //1. 결제 정보 세팅 🌸
      //파라미터값으로 finance, consulting 구분
      const { data } = await getMentoringTicketData({ field: { mentoring_field: 'consulting' } });
      setMentoringTicketsData(data);
      setPrice({
        name: CMentoringTicketName[mentoring_type],
        price: Number(
          data.find(
            (el: TMentoringTicketInfo) => el.mentoring_ticket_name === CMentoringTicketName[mentoring_type],
          ).mentoring_ticket_price,
        ),
      });
    }

    //1. 가격, 상품 정보 받음
    if (params.type === 'exercise_membership') {
      //만약 결제 이력이 있는 경우에는 가격 및 기간 변동 필요
      const { success, data } = await getDrillPaymentInfo();
      if (success) {
        setPrice({
          name: `멤버십 (${data.month}개월)`,
          price: data.amount,
        });
      }
    }

    if (params.type === 'excel') {
      setPrice({
        name: `엑셀 강의 영상`,
        price: EXCEL_VIDEO_PRICE,
      });
    }

    if (params.type === 'interview-master-class-basic') {
      setPrice({
        name: `전략컨설팅 인터뷰 MasterClass [기초편]`,
        price: INTERVIEW_MASTER_CLASS_BASIC_PRICE,
      });
    }

    //임의 5회 결제창은 하드코딩
    if (params.type === 'mentoring') {
      setCredit(1000);
      setPrice({
        name: '멘토링 수강권 5회권',
        price: 1500,
      });
    }

    //케이스 노트 구매 결제창
    if (params.type === 'case') {
      if (p_type === 'mentor') {
        //멘토의 해설 개별 구매일 때
        const { success, data } = await getMentorAnswerPrice(Number(id));
        if (success) {
          setCredit(data.CS);
          setPrice({
            name: data.mentor_answer.product_name,
            price: data.mentor_answer.product_credit,
            items: [{ id: data.mentor_answer.id }],
          });
        }
      } else {
        // 상품 구매일 떄
        const { success, data } = await getCasePrice(Number(id), { season });
        if (success) {
          setCredit(data.CS);

          if (p_type === 'single') {
            setPrice({
              name: data.careerstep_name,
              price: data.careerstep_credit,
              items: data.careerstep_answer,
            });
          } else {
            setPrice({
              name: data.season_package_name,
              price: data.season_package_credit,
              items: data.season_package_answer,
            });
          }
        }
      }
    }
  };

  const makeMethod = (method: string) => {
    const finded = payMethods.find((m) => m.value === method);
    // if (finded?.icon) return <Grid isInline image={payIcons[finded?.icon]} imageSize='contain' imagePosition='center left' width='50px' height='20px' />;
    // else if (finded?.text) return <Text>{finded?.text}</Text>;
    return <Text>{finded?.text}</Text>;
  };

  const makeMobileRedirectUrl = (host: string) => {
    if (params.type === 'case') {
      return `${host}/purchase/${params.type}?complete=true&id=${id}&season=${season}&p_type=${p_type}`;
    } else if (params.type === 'mentoring_ticket' || params.type === 'finance_mentoring_ticket') {
      //8. 이니시스에서 모바일 결제 완료됐을 때 리다이렉트 url 🌸
      return `${host}/purchase/${params.type}?mentoring_type=${mentoring_type}&complete=true`;
    } else {
      return `${host}/purchase/${params.type}?complete=true`;
    }
  };

  const checkForm = () => {
    if (!method) {
      showAlert(undefined, '결제', '결제 수단을 입력해주세요.');
      return false;
    }
    if (!isAgree) {
      showAlert(undefined, '결제', '필수 동의사항을 확인해주세요.');
      return false;
    }
    return {
      pg: 'html5_inicis',
      pay_method: method,
      name: price?.name,
      amount:
        //2. 결제하기 눌렀을 때 checkForm하면서 조건만족하는지 검증 🌸
        params.type === 'excel' ||
        params.type === 'exercise_membership' ||
        params.type === 'interview-master-class-basic' ||
        params.type === 'mentoring_ticket' ||
        params.type === 'finance_mentoring_ticket' ||
        params.type === 'finance_frameworks'
          ? price?.price || 1
          : (price?.price || 1) * credit,
      buyer_email: currentUser?.userid,
      buyer_name: currentUser?.fullname,
      buyer_tel: currentUser?.phone,
      merchant_uid: '',
      //⭐⭐⭐ 모바일 ⭐⭐⭐
      m_redirect_url: makeMobileRedirectUrl(window.location.origin),
      display: { card_quota: [2, 3] },
    };
  };

  //고객이 결제를 완료한 후 실행되는 함수(IMP.request_pay 메서드의 callback함수)
  const verify = (merchant_uid: string) => async (rsp: any) => {
    //결제 성공 시 로직
    //결제요청 성공 (rsp.success === true) 이면 함수 차례로 호출 (verifyPayment() , completePayment())
    if (rsp.success) {
      if (params.type === 'case') {
        try {
          //거래 검증
          //요청이 성공 했을 때 결제번호(imp_uid)와 주문번호(merchant_uid)를 서버에 전달하는 로직
          await verifyPayment({
            result: rsp.success,
            error_message: rsp.error_msg,
            imp_uid: rsp.imp_uid,
            merchant_uid,
          });
          //가맹점 서버 결제 API 성공시 로직
          //해당 거래의 결제금액의 위변조 여부를 검증하고 결제 정보를 데이터베이스에 저장하기 위해
          //결제 번호(imp_uid)와 주문번호(merchant_uid)를 전달받는 API endpoint
          const { success } = await completePayment({
            imp_uid: rsp.imp_uid,
            merchant_uid,
          });
          if (success) {
            //결제가 정상적으로 이루어졌을 때, 결제 완료 페이지 렌더링을 위해
            //completeInfo 의 상태값을 바꿔준다
            setCompleteInfo({
              name: price?.name || '',
              price: (price?.price || 1) * credit,
              credit: price?.price || 1,
              method,
            });
          }
          //결제 완료 페이지로 이동
          history.replace(`?complete=true`);
        } catch (e) {
          //결제 실패 시 로직
          showAlert(
            undefined,
            '',
            '결제 정보 인증에 실패하였습니다. 결제가 완료되었다면 관리자에게 문의해주세요.',
          );
          throw e;
        }
      } else if (params.type === 'mentoring') {
        try {
          //거래 검증
          const { data } = await verifyPayment({
            result: rsp.success,
            error_message: rsp.error_msg,
            imp_uid: rsp.imp_uid,
            merchant_uid,
          });
          if (data.status === 'success') {
            setCompleteInfo({
              name: price?.name || '',
              price: (price?.price || 1) * credit,
              credit: price?.price || 1,
              method,
            });
          }
          //결제 완료 페이지로 이동
          history.replace(`?complete=true`);
        } catch (e) {
          //결제 실패 시 로직
          showAlert(
            undefined,
            '',
            '결제 정보 인증에 실패하였습니다. 결제가 완료되었다면 관리자에게 문의해주세요.',
          );
          throw e;
        }
      } else if (
        params.type === 'excel' ||
        params.type === 'exercise_membership' ||
        params.type === 'mentoring_ticket' || //4. PG사의 결제창을 호출 하면서 거래 검증 🌸
        params.type === 'finance_mentoring_ticket' ||
        params.type === 'finance_frameworks' ||
        params.type === 'interview-master-class-basic'
      ) {
        try {
          const { data } = await drillVerifyPayment({
            result: rsp.success,
            error_message: rsp.error_msg,
            imp_uid: rsp.imp_uid,
            merchant_uid,
          });
          if (data.status === 'success') {
            setCompleteInfo({
              name: price?.name || '',
              price: price?.price || 1,
              // credit: price?.price || 1,
              method,
            });
            dispatch(setUserInfo(data.user as TUser));
          }
          //결제 완료 페이지로 이동
          history.replace(`?complete=true`);
        } catch (e) {
          //결제 실패 시 로직
          showAlert(
            undefined,
            '',
            '결제 정보 인증에 실패하였습니다. 결제가 완료되었다면 관리자에게 문의해주세요.',
          );
          throw e;
        }
      }
    } else {
      showAlert(
        undefined,
        '',
        rsp.error_msg === '사용자가 결제를 취소하셨습니다' ? '결제가 취소되었습니다.' : rsp.error_msg,
      );
    }
  };

  const payment = async () => {
    //가맹점 식별 코드 추가
    const { IMP } = window as any;
    IMP.init('imp73372599');

    //checkForm 에서 결제 수단 결제 동의 모두 체크 했으면 params 리턴 (여기서 merchant_uid는 빈 문자열이다.)
    const purchasingParams = checkForm();

    if (purchasingParams) {
      let response;
      //3. 결제하기 눌렀을 때 이니시스 결제창 뜨기 직전 🌸

      if (params.type === 'finance_mentoring_ticket') {
        response = await createPaymentLog(
          {
            pay_method: purchasingParams.pay_method,
            mentoring_ticket_id: mentoringTicketsData?.find(
              (el: TMentoringTicketInfo) => el.mentoring_ticket_name === CMentoringTicketName[mentoring_type],
            )?.id,
          },
          'ticket',
        );
      }
      if (params.type === 'finance_frameworks') {
        response = await createPaymentLog(
          {
            pay_method: purchasingParams.pay_method,
            amount: FINANCE_FRAMEWORKS_PRICE,
          },
          'finance_framework',
        );
      }

      //createPaymentLog(거래 로그 생성) 엔드포인트에 아래 객체를 바디로 전달
      if (params.type === 'mentoring_ticket') {
        response = await createPaymentLog(
          {
            pay_method: purchasingParams.pay_method,
            mentoring_ticket_id: mentoringTicketsData?.find(
              (el: TMentoringTicketInfo) => el.mentoring_ticket_name === CMentoringTicketName[mentoring_type],
            )?.id,
          },
          'ticket',
        );
      }

      if (params.type === 'exercise_membership') {
        response = await createPaymentLog(
          {
            pay_method: purchasingParams.pay_method,
            is_drill_membership: true,
          },
          'drill',
        );
      }
      if (params.type === 'excel') {
        response = await createPaymentLog(
          {
            pay_method: purchasingParams.pay_method,
            amount: EXCEL_VIDEO_PRICE,
            is_excel_video_lecture: true,
          },
          'excel_video',
        );
      }

      if (params.type === 'interview-master-class-basic') {
        response = await createPaymentLog(
          {
            pay_method: purchasingParams.pay_method,
            amount: INTERVIEW_MASTER_CLASS_BASIC_PRICE,
            is_interview_master_class_basic: true,
          },
          'interview-master-class-basic',
        );
      }

      if (params.type === 'mentoring') {
        response = await createPaymentLog(
          {
            pay_method: purchasingParams.pay_method,
            //멘토링 5회권 하드코딩 credit, amount
            credit: 5,
            amount: 1500000,
            // mentor_answer:[]
          },
          'mentoring',
        );
      }

      if (params.type === 'case') {
        response = await createPaymentLog({
          pay_method: purchasingParams.pay_method,
          credit: price?.price || 1,
          mentor_answer: price?.items?.map((i) => i.id) || [],
          is_mentor_answer_package: p_type === 'package',
        });
      }

      if (response?.success) {
        //IMP.request_pay를 호출하기 전에 서버에 주문 정보를 전달하고 서버가 생성한 주문 번호를 params.merchant_uid 속성에 지정하기를 권장한다
        //호출에 성공하면 params 객체에 data로 받은 merchant_uid값을 넣어준다
        purchasingParams.merchant_uid = response.data.merchant_uid;
        // request_pay(param, callback) PG사의 결제창을 호출.
        //첫번째 인자 param에 결제 요청에 필요한 속성과 값을 담는다
        //IMP.request_pay 메서드 실행, 콜백함수로는 verify()에 merchant_uid를 인자로 넣음
        IMP.request_pay(purchasingParams, verify(response.data.merchant_uid));
      }
    }
  };

  //결제 심사 전 임의 모달창
  const [showModal, setShowModal] = useState(false);
  const cannotUsePayment = async () => {
    const params = checkForm();
    if (params) {
      setShowModal(true);
    }
  };

  const mobilePayment = async () => {
    try {
      //거래 검증
      //요청이 성공 했을 때 결제번호(imp_uid)와 주문번호(merchant_uid)를 서버에 전달하는 로직
      if (
        params.type === 'excel' ||
        params.type === 'exercise_membership' ||
        params.type === 'mentoring_ticket' || //6. 모바일 거래 검증 🌸
        params.type === 'finance_mentoring_ticket' ||
        params.type === 'finance_frameworks'
      ) {
        const { data } = await drillVerifyMobilePayment({
          imp_success: `${imp_success}`,
          imp_uid: imp_uid,
          merchant_uid: merchant_uid,
        });
        if (data.status === 'success') {
          dispatch(setUserInfo(data.user as TUser));
        }
      } else {
        await verifyMobilePayment({
          imp_success: `${imp_success}`,
          imp_uid: imp_uid,
          merchant_uid: merchant_uid,
        });
      }
      // 가맹점 서버 결제 API 성공시 로직
      //해당 거래의 결제금액의 위변조 여부를 검증하고 결제 정보를 데이터베이스에 저장하기 위해
      //결제 번호(imp_uid)와 주문번호(merchant_uid)를 전달받는 API endpoint
      if (params.type === 'case') {
        const { success } = await completePayment({
          imp_uid: imp_uid,
          merchant_uid: merchant_uid,
        });
      }
      //결제 완료 페이지로 이동
      history.replace(`?complete=true`);
    } catch (e) {
      //결제 실패 시 로직
      showAlert(
        undefined,
        '',
        '결제 정보 인증에 실패하였습니다. 결제가 완료되었다면 관리자에게 문의해주세요.',
      );
      throw e;
    }
  };

  useEffect(() => {
    //모바일 구매 시 이니시스에서 넘겨받는 URL 활용하여 정상 결제 검증
    if (complete && merchant_uid && imp_success) {
      mobilePayment();
    }
    //모바일 & PC 모두 해당 (결제 성공해서 성공 페이지까지 왔는데 completeInfo가 없다는것은 결제 성공 직후가 아니라 리다이렉티되었다는것)
    if (!imp_success && complete) {
      if (!completeInfo) {
        if (params.type === 'case') {
          showAlert(() => history.push(`/case/board`), '', '결제가 완료된 상품입니다.');
        }
        if (
          params.type === 'mentoring' ||
          params.type === 'mentoring_ticket' ||
          params.type === 'finance_mentoring_ticket' ||
          params.type === 'finance_frameworks'
        ) {
          //5. 결제 완료 되었는데 그 페이지에 있다면 🌸
          showAlert(() => history.push(`/user/credit`), '', '결제가 완료된 상품입니다.');
        }
        if (params.type === 'exercise_membership') {
          showAlert(() => history.push(`/case/quizzes`), '', '결제가 완료된 상품입니다.');
        }
        if (params.type === 'excel') {
          showAlert(() => history.push(`/user/credit`), '', '결제가 완료된 상품입니다.');
        }
      }
    } else {
      //complete이 없는 것은 처음 결제 페이지(결제 완료된 페이지 X)
      getProduct();
    }
  }, [location, complete]);

  //모바일 구매 정상 결제 시(conplete) 결제 완료 페이지에 가격 및 상품 데이터가 잘 들어오도록 하기 위해
  useEffect(() => {
    if (`${imp_success}` === 'true' && price) {
      //7. 모바일 구매 시 정상 결제 시(conplete) 결제 완료 페이지에 가격 및 상품 데이터 세팅을 위해 🌸

      setCompleteInfo({
        name: price?.name || '',
        price: credit ? (price?.price || 1) * credit : price?.price || 1,
        credit: price?.price || 1,
        method,
      });
    }
  }, [price]);

  if (!price) {
    return (
      <Grid justify='center' align='center' height='100px' margin='0 0 150px 0' md={{ margin: '0 0 80px 0' }}>
        <Spin spinning />
      </Grid>
    );
  }

  //결제가 완료되었을 경우 결제 완료 페이지
  if (completeInfo) {
    return (
      <Grid justify='center' margin='0 0 150px 0' md={{ margin: '0 0 50px 0' }}>
        <Grid
          maxWidth={`${CWindowWidth}px`}
          padding='50px'
          md={{ padding: '50px 20px' }}
          sm={{ padding: '16px' }}
        >
          <Grid padding='0 7%' md={{ padding: '0 3%' }} sm={{ padding: '0' }}>
            <Grid>
              <Text bold size='2.8rem' sm={{ size: '2rem' }}>
                결제가 완료되었습니다.
              </Text>
            </Grid>

            <Grid marginTop='40px' sm={{ marginTop: '20px' }}>
              <Text bold size='1.2rem'>
                결제 정보
              </Text>
            </Grid>

            <Grid marginTop='13px' sm={{ marginTop: '8px' }}>
              <Grid isNoWrap backgroundColor={Colors.grey0} padding='16px' gap='18px' maxWidth='400px'>
                <Grid maxWidth='380px' width='100%'>
                  <Grid
                    padding='8px 0'
                    justify='space-between'
                    gap='8px'
                    isNoWrap
                    style={{ borderBottom: `1px solid ${Colors.grey1}` }}
                  >
                    <Grid isInline width='70px'>
                      <Text>상품명</Text>
                    </Grid>
                    <Grid isInline minWidth='150px' justify='flex-end'>
                      <Text>
                        {completeInfo.name === '시즌 Pack'
                          ? // ? '케이스 노트 시즌 Pack (10개 케이스)'
                            '케이스 노트 Pack (10개 케이스)'
                          : completeInfo.name}
                      </Text>
                    </Grid>
                  </Grid>
                  <Grid
                    padding='8px 0'
                    justify='space-between'
                    gap='8px'
                    isNoWrap
                    style={{ borderBottom: `1px solid ${Colors.grey1}` }}
                  >
                    <Grid isInline width='70px'>
                      <Text>상품 가격</Text>
                    </Grid>
                    {/* <Grid isInline minWidth='150px' justify='flex-end'><Text>{makeComma(completeInfo.credit)} CS</Text></Grid> */}
                    <Grid isInline minWidth='150px' justify='flex-end'>
                      <Text>{makeComma(completeInfo.price)} 원</Text>
                    </Grid>
                  </Grid>
                  <Grid
                    padding='8px 0'
                    justify='space-between'
                    gap='8px'
                    isNoWrap
                    style={{ borderBottom: `1px solid ${Colors.grey1}` }}
                  >
                    <Grid isInline width='70px'>
                      <Text color={Colors.danger}>사용 적립금</Text>
                    </Grid>
                    <Grid isInline minWidth='150px' justify='flex-end'>
                      <Text color={Colors.danger}>0 원</Text>
                    </Grid>
                  </Grid>
                  <Grid
                    padding='8px 0'
                    justify='space-between'
                    gap='8px'
                    isNoWrap
                    style={{ borderBottom: `1px solid ${Colors.grey1}` }}
                  >
                    <Grid isInline width='70px'>
                      <Text>구매 적립금</Text>
                    </Grid>
                    {/* <Grid isInline minWidth='150px' justify='flex-end'><Text>{makeComma(completeInfo.credit)} CS</Text></Grid> */}
                    <Grid isInline minWidth='150px' justify='flex-end'>
                      <Text>0 원</Text>
                    </Grid>
                  </Grid>
                  <Grid
                    padding='8px 0'
                    justify='space-between'
                    gap='8px'
                    isNoWrap
                    style={{ borderBottom: `1px solid ${Colors.grey1}` }}
                  >
                    <Grid isInline width='70px'>
                      <Text color={SecondaryColor}>총 결제 금액</Text>
                    </Grid>
                    <Grid isInline minWidth='150px' justify='flex-end'>
                      <Text color={SecondaryColor}>{makeComma(completeInfo.price)}원</Text>
                    </Grid>
                  </Grid>
                  <Grid
                    padding='8px 0'
                    justify='space-between'
                    gap='8px'
                    isNoWrap
                    style={{ borderBottom: `1px solid ${Colors.grey1}` }}
                  >
                    <Grid isInline width='70px'>
                      <Text>결제 수단</Text>
                    </Grid>
                    <Grid isInline minWidth='150px' justify='flex-end'>
                      {makeMethod(completeInfo.method)}
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>

            <Grid marginTop='30px' width='400px' sm={{ marginTop: '15px' }}>
              {/* 결제 전 페이지로 돌아가기 */}
              {/* //9. 결제 완료 페지서 버튼 🌸 */}
              <Button
                type='primary'
                className='large'
                style={{ width: '100%' }}
                onClick={() => {
                  params.type === 'case' && history.push(`/case/board`);
                  (params.type === 'mentoring' ||
                    params.type === 'mentoring_ticket' ||
                    params.type === 'finance_mentoring_ticket' ||
                    params.type === 'finance_frameworks') &&
                    history.push(`/user/credit`);
                  params.type === 'exercise_membership' && history.push(`/case/quizzes`);
                  params.type === 'excel' && history.push(ERoute.ExcelClassPlayer.replace(':id', '0'));
                  params.type === 'interview-master-class-basic' &&
                    history.push(ERoute.LectureInterviewMasterClassBasicVideoPlayer.replace(':videoId', '1'));
                }}
              >
                {params.type === 'case'
                  ? `Case 페이지로 돌아가기`
                  : params.type === 'mentoring' ||
                    params.type === 'mentoring_ticket' ||
                    params.type === 'finance_mentoring_ticket' ||
                    params.type === 'finance_frameworks'
                  ? `수강권 현황 페이지로 돌아가기`
                  : params.type === 'exercise_membership'
                  ? `연습문제 리스트 페이지로 돌아가기`
                  : params.type === 'excel'
                  ? '엑셀 영상 강의 보러가기'
                  : params.type === 'interview-master-class-basic'
                  ? 'MasterClass 강의 보러가기'
                  : ''}
              </Button>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    );
  }

  //결제 전 결제 정보 띄워주는 페이지
  return (
    <Grid justify='center' margin='0 0 150px 0' md={{ margin: '0 0 80px 0' }}>
      <Grid
        maxWidth={`${CWindowWidth}px`}
        padding='50px'
        md={{ padding: '50px 20px' }}
        sm={{ padding: '16px' }}
      >
        <Grid padding='0 7%' md={{ padding: '0 3%' }} sm={{ padding: '0' }}>
          <Grid>
            <Text bold size='2.8rem'>
              결제
            </Text>
          </Grid>
          <Grid marginTop='40px'>
            <Text bold size='1.2rem'>
              결제 정보
            </Text>
          </Grid>

          <Grid marginTop='13px' maxWidth='500px'>
            <Grid isNoWrap backgroundColor={Colors.grey0} padding='16px' gap='18px'>
              <Grid isInline maxWidth='350px' sm={{ width: '100%' }}>
                <Grid
                  padding='8px 0'
                  justify='space-between'
                  gap='8px'
                  isNoWrap
                  style={{ borderBottom: `1px solid ${Colors.grey1}` }}
                >
                  <Grid isInline width='70px'>
                    <Text>상품명</Text>
                  </Grid>
                  <Grid isInline minWidth='150px' justify='flex-end'>
                    <Text>
                      {price.name === '시즌 Pack'
                        ? // ? '케이스 노트 시즌 Pack (10개 케이스)'
                          '케이스 노트 Pack (10개 케이스)'
                        : price.name}
                    </Text>
                  </Grid>
                </Grid>
                <Grid
                  padding='8px 0'
                  justify='space-between'
                  gap='8px'
                  isNoWrap
                  style={{ borderBottom: `1px solid ${Colors.grey1}` }}
                >
                  <Grid isInline width='70px'>
                    <Text>상품 가격</Text>
                  </Grid>
                  {/* <Grid isInline minWidth='150px' justify='flex-end'><Text>{price.price} CS</Text></Grid> */}
                  <Grid isInline minWidth='150px' justify='flex-end'>
                    <Text>{!credit ? makeComma(price.price) : makeComma(credit * price.price)}원</Text>
                  </Grid>
                </Grid>
                <Grid
                  padding='8px 0'
                  justify='space-between'
                  gap='8px'
                  isNoWrap
                  style={{ borderBottom: `1px solid ${Colors.grey1}` }}
                >
                  <Grid isInline width='70px'>
                    <Text>보유 적립금</Text>
                  </Grid>
                  <Grid isInline minWidth='150px' justify='flex-end'>
                    <Text>0 원</Text>
                  </Grid>
                </Grid>
                {/*
                    <Grid padding='8px 0' justify='space-between' gap='8px' isNoWrap style={{ borderBottom: `1px solid ${Colors.grey1}` }}>
                      <Grid isInline width='70px'><Text color={Colors.danger}>사용 크레딧</Text></Grid>
                      <Grid isInline minWidth='150px' justify='flex-end' align='center' gap='5px'>
                        <Grid isInline>
                          <Input size="small" placeholder="0" style={{ width: 100, color: Colors.danger }} />
                        </Grid>
                        <Text color={Colors.danger}>CS</Text>
                        <Grid justify='flex-end' position='relative' align='center' display='none' sm={{ display: 'flex' }} style={{ left: 10 }}>
                          <Checkbox checked={useAll} onChange={e => setUseAll(e.target.checked)}>
                            <Text color={Colors.grey4}>모두 사용</Text>
                          </Checkbox>
                        </Grid>
                      </Grid>
                    </Grid>
                  */}
                <Grid
                  padding='8px 0'
                  justify='space-between'
                  gap='8px'
                  isNoWrap
                  style={{ borderBottom: `1px solid ${Colors.grey1}` }}
                >
                  <Grid isInline width='70px'>
                    <Text>사용 적립금</Text>
                  </Grid>
                  {/* <Grid isInline minWidth='150px' justify='flex-end'><Text>{price.price} CS</Text></Grid> */}
                  <Grid isInline minWidth='150px' justify='flex-end'>
                    <Text>0 원</Text>
                  </Grid>
                </Grid>
                {/* <Grid marginTop='10px' justify='flex-end'>
                  <Text size='0.9rem'>*1 CS = {makeComma(credit)}원</Text>
                </Grid> */}
                <Grid marginTop='25px' justify='space-between' align='center'>
                  <Text bold color={SecondaryColor}>
                    총 결제 금액
                  </Text>
                  <Text bold color={SecondaryColor}>
                    {!credit ? makeComma(price.price) : makeComma(credit * price.price)}원
                  </Text>
                </Grid>
              </Grid>
              {/*
                  <Grid isInline align='center' sm={{ display: 'none' }}>
                    <Checkbox checked={useAll} onChange={e => setUseAll(e.target.checked)}>
                      <Text color={Colors.grey4}>모두 사용</Text>
                    </Checkbox>
                  </Grid>
                */}
            </Grid>
          </Grid>

          <Grid marginTop='40px'>
            <Text bold size='1.2rem'>
              결제 수단
            </Text>
          </Grid>
          <Grid marginTop='15px' padding='0 15px'>
            <Radio.Group value={method} onChange={(e) => setMethod(e.target.value)}>
              <Space direction='vertical'>
                {payMethods.map((method, index) => {
                  //수강권 결제 페이지가 아닐 경우에는 계좌이체 옵션 띄우지 않음
                  if (params.type !== 'mentoring_ticket' && method.text !== '계좌이체') {
                    return (
                      <Radio
                        key={index}
                        value={method.value}
                        style={{
                          height: 30,
                          display: 'flex',
                          alignItems: 'center',
                        }}
                      >
                        {/*
                        method.icon ? (
                          <Grid isInline image={payIcons[method.icon]} imageSize='contain' imagePosition='center left' width='90px' height='25px' marginTop='5px' />
                        ) : (
                          <Grid marginTop='5px'>{method.text}</Grid>
                        )
                        */}
                        <Grid marginTop='5px'>{method.text}</Grid>
                      </Radio>
                    );
                  }
                  //수강권 결제 페이지일 경우에는 계좌이체 옵션 띄움
                  if (params.type === 'mentoring_ticket' || params.type === 'finance_mentoring_ticket') {
                    return (
                      <Radio
                        key={index}
                        value={method.value}
                        style={{
                          height: 30,
                          display: 'flex',
                          alignItems: 'center',
                        }}
                      >
                        <Grid marginTop='5px'>{method.text}</Grid>
                      </Radio>
                    );
                  }
                })}
              </Space>
            </Radio.Group>
          </Grid>

          {method === 'trans' && (
            <Grid marginTop='10px'>
              <Grid
                maxWidth='500px'
                style={{
                  border: `1px ${Colors.grey1} solid`,
                  borderRadius: '5px',
                }}
              >
                <StyledAccountTransferInfo>
                  <p>계좌번호 : 농협은행 301-0286-1957-11 (주)빅스텝에듀케이션 </p>
                  <p>계좌이체를 완료하신 후 우측 하단 챗봇에서 수강권 요청을</p>
                  <p>클릭하여 상품명과 본인의 계정 아이디를 적어주세요.</p>
                </StyledAccountTransferInfo>
              </Grid>
            </Grid>
          )}

          <Grid marginTop='30px'>
            <Grid maxWidth='500px'>
              <Checkbox
                checked={isAgree}
                onChange={(e) => {
                  method !== 'trans' && setIsAgree(e.target.checked);
                }}
                disabled={method === 'trans'}
              >
                <Grid isInline>
                  <Text color={Colors.grey4}>결제 내역을 확인하였으며 구매에 동의합니다.</Text>
                </Grid>
              </Checkbox>
            </Grid>
          </Grid>

          <Grid marginTop='30px'>
            <Grid maxWidth='500px'>
              <Button
                type='primary'
                className='large'
                disabled={method === 'trans'}
                style={{ width: '100%' }}
                onClick={() => {
                  if (params.type === 'excel' && currentUser?.is_active_for_excel_video) {
                    showConfirm(() => {
                      history.push(ERoute.UserCredit);
                    }, '이미 구매한 상품입니다');
                  } else {
                    payment();
                  }
                }}
              >
                결제하기
              </Button>
            </Grid>
          </Grid>

          {/* {params.type !== 'exercise_membership' && (
            <Grid marginTop="20px">
              <a
                target="_blank"
                rel="noopener noreferrer"
                href={CRefundInformation}
              >
                <Text textDecoration="underline" color={Colors.grey2}>
                  환불규정
                </Text>
              </a>
            </Grid>
          )} */}

          {/* <Modal
          className="cannot-use-payment"
          closable={false}
          visible = {showModal}
          footer={<Button onClick = {()=> setShowModal(false)} block>닫기</Button>}
          >
          <div
            // align='center'
            style={{
            lineHeight: "0.8",
            textAlign: "center",
            color: "#4a485e",
            padding:"30px 0",
          }}
          >
            <div className="modal-content">
              <p>현재 결제 기능 심사 중인 관계로</p>
              <p>
                안내드리는 계좌로 송금 후,
                <span style={{ textDecoration: "underline" }}> 계정 ID</span> 및 
                <span style={{ textDecoration: "underline" }}> 구매 희망 상품</span>을
                </p>
              <p>오른쪽 아래 말풍선을 클릭하여 실시간 상담으로 남겨주시면</p>
              <p>결제 처리 및 해설 열람 권한 부여해드리겠습니다.</p>
            </div>

            <div className="modal-content-small"> 
              <p>현재 결제 기능 심사 중인 관계로</p>
              <br />
              <p>안내드리는 계좌로 송금 후,</p>
              <br />
              <p>
                <span style={{ textDecoration: "underline" }}> 계정 ID</span> 및 
                <span style={{ textDecoration: "underline" }}> 구매 희망 상품</span>을
              </p>
              <br />
              <p>오른쪽 아래 말풍선을 클릭하여 </p>
              <br />
              <p>실시간 상담으로 남겨주시면</p>
              <br />
              <p>결제 처리 및 해설 열람 권한 </p>
              <br />
              <p>부여해드리겠습니다.</p>
            </div>
            
            <br />
            <br />
            <br />

            <div className="account-number">
              <p style={{fontWeight:"bolder"}}>계좌번호 : 신한은행 100-034-610626 (주)빅스텝에듀케이션</p>
            </div>
            <div className="account-number-small">
            <p style={{fontWeight:"bolder"}}>계좌번호  </p>
            <p>: 신한은행 100-034-610626</p>
            <p>(주)빅스텝에듀케이션</p>
            </div>
          </div>
          </Modal> */}
        </Grid>
      </Grid>
    </Grid>
  );
};
export default Purchase;

const StyledAccountTransferInfo = styled.div`
  /* margin: auto; */
  padding: 10px;
  p {
    margin-bottom: 0;
    @media screen and ${device.xs} {
      font-size: 11px;
    }
  }
`;
